import { apiClient } from '../services/api';

/**
 * Форматировать имя модели для файла (убрать спецсимволы)
 */
function formatModelName(model?: string): string {
  if (!model) return 'unknown';

  // Убираем спецсимволы и заменяем пробелы на подчёркивания
  return model
    .replace(/[^a-zA-Z0-9-_]/g, '_')
    .replace(/_+/g, '_')
    .replace(/^_|_$/g, '')
    .substring(0, 30); // Ограничиваем длину
}

/**
 * Сгенерировать имя файла для скачивания
 * Формат: StarAI_[MODEL]_[type]-[timestamp].[ext]
 * @param type - Тип файла: 'image' | 'video'
 * @param model - Название модели (опционально)
 * @param extension - Расширение файла (по умолчанию png для image, mp4 для video)
 */
export function generateDownloadFileName(
  type: 'image' | 'video',
  model?: string,
  extension?: string
): string {
  const timestamp = new Date().toISOString().replace(/[:.]/g, '-').slice(0, 19);
  const modelName = formatModelName(model);
  const ext = extension || (type === 'video' ? 'mp4' : 'png');

  return `StarAI_${modelName}_${type}-${timestamp}.${ext}`;
}

/**
 * Скачать изображение через canvas
 * Работает с уже загруженными/кэшированными изображениями
 */
async function downloadImageViaCanvas(url: string, fileName: string): Promise<void> {
  return new Promise((resolve, reject) => {
    const img = new Image();
    img.crossOrigin = 'anonymous';

    img.onload = () => {
      try {
        // Создаём canvas с размерами изображения
        const canvas = document.createElement('canvas');
        canvas.width = img.naturalWidth;
        canvas.height = img.naturalHeight;

        // Рисуем изображение на canvas
        const ctx = canvas.getContext('2d');
        if (!ctx) {
          reject(new Error('Canvas context not available'));
          return;
        }
        ctx.drawImage(img, 0, 0);

        // Конвертируем в blob
        canvas.toBlob((blob) => {
          if (!blob) {
            reject(new Error('Failed to create blob'));
            return;
          }

          // Создаём ссылку для скачивания
          const blobUrl = URL.createObjectURL(blob);
          const link = document.createElement('a');
          link.href = blobUrl;
          link.download = fileName;
          link.style.display = 'none';
          document.body.appendChild(link);
          link.click();
          document.body.removeChild(link);

          // Освобождаем память
          URL.revokeObjectURL(blobUrl);
          resolve();
        }, 'image/png');
      } catch (error) {
        reject(error);
      }
    };

    img.onerror = () => {
      // Fallback на прокси если canvas не сработал
      downloadViaProxy(url, fileName).then(resolve).catch(reject);
    };

    img.src = url;
  });
}

/**
 * Скачать файл через прокси на бэкенде (использует apiClient с авторизацией)
 * Для видео и файлов где canvas не работает
 */
async function downloadViaProxy(url: string, fileName: string): Promise<void> {
  const blob = await apiClient.storage.proxyDownload(url, fileName);
  const blobUrl = URL.createObjectURL(blob);

  const link = document.createElement('a');
  link.href = blobUrl;
  link.download = fileName;
  link.style.display = 'none';
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);

  URL.revokeObjectURL(blobUrl);
}

/**
 * Скачать файл в браузер (для использования вне Mini App)
 * Для изображений использует canvas (работает с уже загруженными картинками)
 * Для видео использует прокси на бэкенде
 * @param url - URL файла для скачивания
 * @param fileName - Имя файла для сохранения
 */
export async function downloadFileToBrowser(url: string, fileName: string): Promise<void> {
  const isSvg = /\.svg(\?|$)/i.test(fileName) || /\.svg(\?|$)/i.test(url);
  const isImage = /\.(png|jpg|jpeg|gif|webp)$/i.test(fileName) ||
                  /\.(png|jpg|jpeg|gif|webp)/i.test(url);
  const isVideo = /\.(mp4|webm|mov)$/i.test(fileName) ||
                  /\.(mp4|webm|mov)/i.test(url);

  if (isSvg) {
    // SVG нельзя скачивать через canvas (он растеризует) — используем прокси
    await downloadViaProxy(url, fileName);
  } else if (isImage) {
    // Для растровых изображений: используем canvas (работает с кэшированными картинками)
    await downloadImageViaCanvas(url, fileName);
  } else if (isVideo) {
    // Для видео: используем прокси (видео слишком большие для canvas)
    await downloadViaProxy(url, fileName);
  } else {
    // Для остальных файлов: используем прокси
    await downloadViaProxy(url, fileName);
  }
}
